import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import { getOnlineClient, resetOnlineClient } from '@/online/colyseusClient';
import { setOnlineRoom } from '@/online/session';
import { getRandomUniqueNames } from '@/config/playerNames';
import { createBackButton, createButton, setButtonEnabled } from '@/ui/buttons';
import { escapeHtml } from '@/ui/format';
import { isMobileDevice } from '@/utils/mobile';
import { getDiscordDisplayName } from '@/discord';
import { captureError } from '@/sentry';
import { KICK_COOLDOWN_MS } from '@/config/online';
import { getDeviceId } from '@/online/deviceId';
import { trackJoinFlowStart } from '@/services/analytics';

export interface JoinSceneData {
  roomId?: string;
  notice?: string;
}

type JoinState = 'idle' | 'connecting' | 'error';

export class JoinScene extends Phaser.Scene {
  private joinState: JoinState = 'idle';
  private lastError: string | null = null;
  private roomIdValue: string = '';
  private nameValue: string = '';
  private nameInput: Phaser.GameObjects.DOMElement | null = null;
  private codeInput: Phaser.GameObjects.DOMElement | null = null;
  private formPanel: Phaser.GameObjects.Rectangle | null = null;
  private formLabels: Phaser.GameObjects.Text[] = [];
  private joinButton: Phaser.GameObjects.Container | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private isMobile: boolean = false;
  private notice: string | null = null;

  constructor() {
    super({ key: 'JoinScene' });
  }

  init(data?: JoinSceneData): void {
    this.joinState = 'idle';
    this.lastError = null;
    this.roomIdValue = data?.roomId?.trim() ?? this.roomIdValue;
    const fallbackName = getDiscordDisplayName() || (getRandomUniqueNames(1)[0] ?? 'Player');
    this.nameValue = this.nameValue || fallbackName;
    this.isMobile = isMobileDevice();
    this.notice = data?.notice ?? null;
  }

  create(): void {
    // Track join flow start
    trackJoinFlowStart();

    this.cameras.main.fadeIn(300, 5, 7, 22);
    drawArcadeBackground(this);

    const panel = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 700, 450, THEME.colors.backgroundLightHex, 0.9);
    panel.setStrokeStyle(3, THEME.colors.magentaHex);

    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 160, 'JOIN LOBBY', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.titleLarge,
      color: THEME.colors.cyan,
    }).setOrigin(0.5);
    addTextGlow(title, THEME.colors.cyan, 15);

    if (this.notice) {
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 110, this.notice, {
        fontFamily: THEME.fonts.body,
        fontSize: THEME.sizes.body,
        color: THEME.colors.yellow,
        align: 'center',
        wordWrap: { width: 600 },
      }).setOrigin(0.5);
    }

    this.createForm();

    this.joinButton = createButton(this, {
      x: GAME_WIDTH / 2,
      y: GAME_HEIGHT / 2 + 100,
      text: 'JOIN',
      onClick: () => void this.join(),
      style: 'primary',
      buttonType: 'normal',
    });

    this.statusText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 160, '', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    createBackButton(this, {
      x: 110,
      y: 70,
      onClick: () => this.goBack(),
    });

    this.input.keyboard?.on('keydown-ESC', () => this.goBack());
    this.input.keyboard?.on('keydown-ENTER', () => {
      if (this.joinState !== 'connecting') void this.join();
    });
  }

  private createForm(): void {
    const labelX = GAME_WIDTH / 2 - 280;
    const inputX = GAME_WIDTH / 2 + 40;
    const nameY = GAME_HEIGHT / 2 - 50;
    const codeY = GAME_HEIGHT / 2 + 20;

    const nameLabel = this.add.text(labelX, nameY, 'NAME', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
    }).setOrigin(0, 0.5);

    const codeLabel = this.add.text(labelX, codeY, 'ROOM CODE', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
    }).setOrigin(0, 0.5);

    this.formLabels = [nameLabel, codeLabel];

    this.nameInput = this.createDomInput(inputX, nameY, this.nameValue, (v) => { this.nameValue = v; }, 'name');
    this.codeInput = this.createDomInput(inputX, codeY, this.roomIdValue, (v) => { this.roomIdValue = v; }, 'code');
  }

  private createDomInput(
    x: number,
    y: number,
    value: string,
    onChange: (value: string) => void,
    kind: 'name' | 'code',
  ): Phaser.GameObjects.DOMElement {
    const width = 380;
    const extraAttrs = kind === 'code'
      ? 'autocorrect="off" autocapitalize="none" spellcheck="false" autocomplete="off"'
      : 'autocorrect="off" autocapitalize="words" spellcheck="false" autocomplete="name"';

    const safeValue = escapeHtml(value).replace(/"/g, '&quot;');
    const html = `
      <input
        type="text"
        value="${safeValue}"
        ${extraAttrs}
        style="
          width: ${width}px;
          padding: 10px 12px;
          font-size: 18px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          color: #e9f3ff;
          background: rgba(5, 7, 22, 0.75);
          border: 2px solid rgba(0, 255, 255, 0.45);
          border-radius: 12px;
          outline: none;
        "
      />
    `;

    const el = this.add.dom(x, y).createFromHTML(html);
    el.setOrigin(0.5, 0.5);
    el.setDepth(2000);
    const input = (el.node as HTMLElement)?.querySelector('input') as HTMLInputElement | null;
    if (input) {
      input.addEventListener('input', () => onChange(input.value));
    }
    return el;
  }

  private getDomInputValue(dom: Phaser.GameObjects.DOMElement | null, fallback: string): string {
    const input = (dom?.node as HTMLElement)?.querySelector('input') as HTMLInputElement | null;
    return typeof input?.value === 'string' ? input.value : fallback;
  }

  private async join(): Promise<void> {
    this.lastError = null;
    this.joinState = 'connecting';
    if (this.joinButton) setButtonEnabled(this.joinButton, false);
    this.updateStatus('Connecting...');

    const roomId = this.getDomInputValue(this.codeInput, this.roomIdValue).trim();
    let name = this.getDomInputValue(this.nameInput, this.nameValue).trim();
    if (!name) name = getRandomUniqueNames(1)[0] ?? 'Player';
    this.roomIdValue = roomId;
    this.nameValue = name;

    if (!roomId) {
      this.joinState = 'error';
      this.lastError = 'Please enter a room code';
      if (this.joinButton) setButtonEnabled(this.joinButton, true);
      this.updateStatus(this.lastError);
      return;
    }

    try {
      const client = getOnlineClient();
      const room = await client.joinById(roomId, { name, deviceId: getDeviceId() });

      // Suppress Colyseus warning - seat_assigned is handled via state sync in LobbyScene
      room.onMessage('seat_assigned', () => {});

      // Check for join rejection (cooldown)
      room.onMessage('join_rejected', (payload: { reason?: string; remainingMs?: number }) => {
        if (payload.reason === 'cooldown') {
          room.leave();
          resetOnlineClient();
          this.joinState = 'error';
          const seconds = Math.ceil((payload.remainingMs ?? KICK_COOLDOWN_MS) / 1000);
          this.lastError = `You were recently kicked. Try again in ${seconds}s.`;
          if (this.joinButton) setButtonEnabled(this.joinButton, true);
          this.updateStatus(this.lastError);
        }
      });

      setOnlineRoom(room);
      this.scene.start('LobbyScene', { role: 'guest', roomId, name });
    } catch (err) {
      resetOnlineClient();
      this.joinState = 'error';

      // Handle specific error codes
      const errorCode = (err as { code?: number })?.code;
      if (errorCode === 4001) {
        this.lastError = 'Lobby is full. No seats available.';
      } else if (errorCode === 4005) {
        this.lastError = 'You were recently kicked. Try again later.';
      } else if (errorCode === 4006) {
        this.lastError = 'Game in progress. Wait for them to finish.';
      } else {
        captureError(err, { scope: 'joinScene', action: 'join' });
        this.lastError = err instanceof Error ? err.message : String(err);
      }

      if (this.joinButton) setButtonEnabled(this.joinButton, true);
      this.updateStatus(this.lastError);
    }
  }

  private updateStatus(text: string): void {
    if (this.statusText) {
      this.statusText.setText(text);
      this.statusText.setColor(this.joinState === 'error' ? THEME.colors.magenta : THEME.colors.textMuted);
    }
  }

  private goBack(): void {
    if (this.isMobile) {
      this.scene.start('MenuScene');
    } else {
      this.scene.start('FriendWebPlayScene');
    }
  }

  shutdown(): void {
    this.nameInput?.destroy();
    this.codeInput?.destroy();
    this.formPanel?.destroy();
    this.formLabels.forEach(l => l.destroy());
    this.nameInput = null;
    this.codeInput = null;
    this.formPanel = null;
    this.formLabels = [];
  }
}
